import { FC, useEffect, useState } from "react";
import { Breadcrumb, Button, Label, Select, TextInput, Spinner, Alert } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { HiHome, HiCog, HiOfficeBuilding, HiCheckCircle } from "react-icons/hi";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import {
  useCreateCompanyMutation,
  useLazyCheckCompanyDomainQuery,
  CompanyCreatePayload,
} from "@/api/companyApi/company";
import { sectorTypeOptions, sectorModelOptions } from "@/constants/company";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

const DOMAIN_REGEX = /^(?!-)[a-zA-Z0-9-]+(?<!-)$/;

const CreateCompanyPage: FC = () => {
  const { t } = useTranslation(["companySetting", "common"]);
  const navigate = useNavigate();
  const user = useCurrentUser();
  const isSuperAdmin = user?.agent_role === agentRoles.super_admin;

  const [createCompany, { isLoading: isCreating }] = useCreateCompanyMutation();
  const [checkDomain, domainCheck] = useLazyCheckCompanyDomainQuery();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm<CompanyCreatePayload>({
    mode: "onChange",
    defaultValues: {
      company_name: "",
      company_website: "",
      company_phone: "",
      company_domain: "",
      country: "",
      sector_type: "",
      sector_model: "",
    },
  });

  const companyDomain = watch("company_domain");
  const [debouncedDomain, setDebouncedDomain] = useState("");

  useEffect(() => {
    const id = setTimeout(() => setDebouncedDomain(companyDomain || ""), 600);
    return () => clearTimeout(id);
  }, [companyDomain]);

  useEffect(() => {
    if (debouncedDomain && DOMAIN_REGEX.test(debouncedDomain)) {
      checkDomain(debouncedDomain);
    }
  }, [debouncedDomain, checkDomain]);

  const onSubmit = async (values: CompanyCreatePayload) => {
    try {
      await createCompany(values).unwrap();
      toast.success(t("companySetting:createCompany.createSuccess"));
      navigate("/company-settings");
    } catch (err: any) {
      toast.error(
        err?.data?.message ||
          err?.data?.error ||
          t("companySetting:createCompany.createError")
      );
    }
  };

  if (!isSuperAdmin) {
    return (
      <NavbarSidebarLayout>
        <div className="px-4 pt-6 pb-12 max-w-3xl mx-auto">
          <Alert color="failure">
            <h3 className="font-semibold">
              {t("companySetting:createCompany.forbiddenTitle")}
            </h3>
            <p className="text-sm mt-1">
              {t("companySetting:createCompany.forbiddenBody")}
            </p>
          </Alert>
        </div>
      </NavbarSidebarLayout>
    );
  }

  const domainFormatValid = companyDomain ? DOMAIN_REGEX.test(companyDomain) : true;
  const domainTaken =
    domainCheck.isError &&
    (domainCheck.error as any)?.status === 409 &&
    domainCheck.originalArgs === debouncedDomain;
  const domainAvailable =
    domainCheck.isSuccess &&
    domainCheck.originalArgs === debouncedDomain &&
    !!debouncedDomain;
  const domainServerError =
    domainCheck.isError &&
    (domainCheck.error as any)?.status !== 409 &&
    domainCheck.originalArgs === debouncedDomain;

  return (
    <NavbarSidebarLayout>
      <div className="px-4 pt-6 pb-12">
        <Breadcrumb className="mb-4">
          <Breadcrumb.Item href="/">
            <div className="flex items-center gap-x-3">
              <HiHome className="text-xl" />
              <span className="dark:text-white">{t("companySetting:home")}</span>
            </div>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/company-settings" className="flex items-center gap-x-2">
              <HiCog />
              <span>{t("companySetting:title")}</span>
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <div className="flex items-center gap-x-2">
              <HiOfficeBuilding />
              <span>{t("companySetting:createCompany.title")}</span>
            </div>
          </Breadcrumb.Item>
        </Breadcrumb>

        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {t("companySetting:createCompany.title")}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t("companySetting:createCompany.description")}
            </p>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
          >
            <div>
              <Label htmlFor="company_name" value={t("companySetting:company.name")} />
              <TextInput
                id="company_name"
                placeholder={t("companySetting:company.namePlaceholder")}
                {...register("company_name", {
                  required: t("companySetting:required") as string,
                })}
                color={errors.company_name ? "failure" : undefined}
                className="mt-1"
              />
              {errors.company_name && (
                <p className="text-sm text-red-600 mt-1">{errors.company_name.message}</p>
              )}
            </div>

            <div>
              <Label
                htmlFor="company_domain"
                value={t("companySetting:createCompany.domainLabel")}
              />
              <TextInput
                id="company_domain"
                placeholder={t("companySetting:createCompany.domainPlaceholder")}
                {...register("company_domain", {
                  required: t("companySetting:required") as string,
                  pattern: {
                    value: DOMAIN_REGEX,
                    message: t("companySetting:createCompany.domainFormatInvalid") as string,
                  },
                })}
                color={
                  errors.company_domain || domainTaken || (companyDomain && !domainFormatValid)
                    ? "failure"
                    : domainAvailable
                      ? "success"
                      : undefined
                }
                className="mt-1"
              />
              {errors.company_domain && (
                <p className="text-sm text-red-600 mt-1">{errors.company_domain.message}</p>
              )}
              {!errors.company_domain && companyDomain && !domainFormatValid && (
                <p className="text-sm text-red-600 mt-1">
                  {t("companySetting:createCompany.domainFormatInvalid")}
                </p>
              )}
              {domainCheck.isFetching && (
                <p className="text-sm text-blue-500 mt-1">
                  {t("companySetting:createCompany.domainChecking")}
                </p>
              )}
              {domainTaken && (
                <p className="text-sm text-red-600 mt-1">
                  {(domainCheck.error as any)?.data?.message || ""}
                </p>
              )}
              {domainServerError && (
                <p className="text-sm text-red-600 mt-1">
                  {(domainCheck.error as any)?.data?.message ||
                    t("companySetting:createCompany.createError")}
                </p>
              )}
              {domainAvailable && (
                <p className="text-sm text-green-600 mt-1 flex items-start">
                  <HiCheckCircle className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                  <span>
                    {t("companySetting:createCompany.domainAvailable")}{" "}
                    <span className="font-medium break-all">{`https://${debouncedDomain}.icont.ai`}</span>
                  </span>
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t("companySetting:createCompany.domainHelp")}
              </p>
            </div>

            <div>
              <Label htmlFor="company_website" value={t("companySetting:company.website")} />
              <TextInput
                id="company_website"
                type="url"
                placeholder={t("companySetting:company.websitePlaceholder")}
                {...register("company_website", {
                  required: t("companySetting:required") as string,
                })}
                color={errors.company_website ? "failure" : undefined}
                className="mt-1"
              />
              {errors.company_website && (
                <p className="text-sm text-red-600 mt-1">{errors.company_website.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="company_phone" value={t("companySetting:company.phone")} />
              <TextInput
                id="company_phone"
                type="tel"
                placeholder={t("companySetting:company.phonePlaceholder")}
                {...register("company_phone", {
                  required: t("companySetting:required") as string,
                })}
                color={errors.company_phone ? "failure" : undefined}
                className="mt-1"
              />
              {errors.company_phone && (
                <p className="text-sm text-red-600 mt-1">{errors.company_phone.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="country" value={t("companySetting:company.country")} />
              <TextInput
                id="country"
                placeholder={t("companySetting:company.countryPlaceholder")}
                {...register("country", {
                  required: t("companySetting:required") as string,
                })}
                color={errors.country ? "failure" : undefined}
                className="mt-1"
              />
              {errors.country && (
                <p className="text-sm text-red-600 mt-1">{errors.country.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="sector_type" value={t("companySetting:company.sectorType")} />
              <Controller
                name="sector_type"
                control={control}
                rules={{ required: t("companySetting:required") as string }}
                render={({ field }) => (
                  <Select
                    id="sector_type"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    color={errors.sector_type ? "failure" : undefined}
                    className="mt-1"
                  >
                    <option value="" disabled>
                      {t("companySetting:company.selectSector")}
                    </option>
                    {sectorTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                )}
              />
              {errors.sector_type && (
                <p className="text-sm text-red-600 mt-1">{errors.sector_type.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="sector_model" value={t("companySetting:company.sectorModel")} />
              <Controller
                name="sector_model"
                control={control}
                rules={{ required: t("companySetting:required") as string }}
                render={({ field }) => (
                  <Select
                    id="sector_model"
                    value={field.value || ""}
                    onChange={(e) => field.onChange(e.target.value)}
                    color={errors.sector_model ? "failure" : undefined}
                    className="mt-1"
                  >
                    <option value="" disabled>
                      {t("companySetting:company.selectSectorModel")}
                    </option>
                    {sectorModelOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                )}
              />
              {errors.sector_model && (
                <p className="text-sm text-red-600 mt-1">{errors.sector_model.message}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                color="gray"
                type="button"
                onClick={() => navigate("/company-settings")}
                disabled={isCreating}
              >
                {t("companySetting:company.cancel")}
              </Button>
              <Button
                type="submit"
                color="blue"
                disabled={isCreating || !isValid || domainTaken || !domainAvailable}
              >
                {isCreating ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    {t("companySetting:createCompany.submitting")}
                  </>
                ) : (
                  t("companySetting:createCompany.submit")
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </NavbarSidebarLayout>
  );
};

export default CreateCompanyPage;
